import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Divider,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import type { EvolutionAttachedPrintable } from '../evolution/evolutionWorkflowTypes';

const typeLabels: Record<string, string> = {
  medical_leave: 'Atestado Médico',
  attendance: 'Declaração de Comparecimento',
  fit_to_work: 'Aptidão ao Trabalho',
  accompaniment: 'Acompanhante',
  custom: 'Personalizado',
};

function cleanFormData(data: any) {
  const cleaned: any = {
    certificate_type: data.certificate_type || 'medical_leave',
    title: data.title || typeLabels[data.certificate_type] || 'Atestado',
    content: data.content || '',
    patient_id: data.patient_id || null,
    professional_id: data.professional_id || null,
    issue_date: data.issue_date || new Date().toISOString().split('T')[0],
    notes: data.notes || null,
  };
  if (data.cid_code) cleaned.diagnosis_code = data.cid_code;
  if (data.cid_description) cleaned.diagnosis = data.cid_description;
  if (data.days_off) cleaned.leave_days = Number(data.days_off) || 0;
  if (data.start_date) cleaned.leave_start_date = data.start_date;
  if (data.end_date) cleaned.leave_end_date = data.end_date;
  Object.keys(cleaned).forEach((key) => {
    if (['title', 'content', 'certificate_type'].includes(key)) {
      if (cleaned[key] === null || cleaned[key] === undefined) {
        cleaned[key] = key === 'title' ? (typeLabels[cleaned.certificate_type] || 'Atestado') : '';
      }
    } else if (cleaned[key] === '' || cleaned[key] === undefined) {
      cleaned[key] = null;
    }
  });
  return cleaned;
}

export interface CertificateFormDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  professionalId: string;
  initialPatient?: any;
  initialProfessional?: any;
  prefillTemplate?: any | null;
  onSuccess: (item: EvolutionAttachedPrintable) => void;
}

const CertificateFormDialog: React.FC<CertificateFormDialogProps> = ({
  open,
  onClose,
  patientId,
  professionalId,
  initialPatient,
  initialProfessional,
  prefillTemplate,
  onSuccess,
}) => {
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profSearch, setProfSearch] = useState('');
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const lastPatientSearchRef = useRef('');
  const lastProfSearchRef = useRef('');

  const [form, setForm] = useState<any>({
    certificate_type: 'medical_leave',
    title: '',
    content: '',
    patient_id: '',
    professional_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    days_off: '',
    start_date: '',
    end_date: '',
    cid_code: '',
    cid_description: '',
    notes: '',
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(getApiUrl('/clinic/certificates/templates'));
        setTemplates(data?.data || []);
      } catch {
        setTemplates([]);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setForm({
      certificate_type: prefillTemplate?.certificate_type || 'medical_leave',
      title: prefillTemplate?.title || '',
      content: prefillTemplate?.content || '',
      patient_id: patientId || '',
      professional_id: professionalId || '',
      issue_date: new Date().toISOString().split('T')[0],
      days_off: '',
      start_date: '',
      end_date: '',
      cid_code: '',
      cid_description: '',
      notes: '',
    });
    setPatientOptions(initialPatient ? [initialPatient] : []);
    setProfOptions(initialProfessional ? [initialProfessional] : []);
    setPatientSearch('');
    setProfSearch('');
    lastPatientSearchRef.current = '';
    lastProfSearchRef.current = '';
    if (prefillTemplate) {
      setForm((f: any) => ({
        ...f,
        certificate_type: prefillTemplate.certificate_type || f.certificate_type,
        title: prefillTemplate.title || f.title,
        content: prefillTemplate.content || f.content,
      }));
    }
  }, [open, patientId, professionalId, initialPatient, initialProfessional, prefillTemplate]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(getApiUrl('/clinic/budgets-search/patients'), { params: { search: patientSearch || '' } });
        if (data?.success !== false) setPatientOptions(data?.data || []);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch, open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(getApiUrl('/clinic/budgets-search/professionals'), { params: { search: profSearch || '' } });
        if (data?.success !== false) setProfOptions(data?.data || []);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [profSearch, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanedData = cleanFormData(form);
      const res = await axios.post(getApiUrl('/clinic/certificates'), cleanedData);
      const saved = res.data?.data ?? res.data;
      const newId = saved?.id ?? saved?.certificate?.id;
      const title = form.title || typeLabels[form.certificate_type] || 'Atestado';
      const preview = String(form.content || '').slice(0, 500);
      const printable: EvolutionAttachedPrintable = {
        clientKey: crypto.randomUUID(),
        category: 'certificate',
        templateId: prefillTemplate?.id,
        templateName: title,
        bodyText: preview + (String(form.content || '').length > 500 ? '…' : ''),
        linkedDocumentId: newId,
        linkedDocumentType: 'certificate',
      };
      onSuccess(printable);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (template: any) => {
    setForm((f: any) => ({
      ...f,
      certificate_type: template.certificate_type || f.certificate_type,
      title: template.title || f.title,
      content: template.content || f.content,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Novo atestado</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Mesmo cadastro da página <strong>Atestados</strong>. Ao salvar, o documento é registrado e pode ser impresso como na central de atestados.
        </Alert>
        {!prefillTemplate && templates.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b' }}>Modelos rápidos</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {templates.map((t: any) => (
                <Chip key={t.id} label={t.title} clickable onClick={() => applyTemplate(t)}
                  sx={{ bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 500 }} />
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={form.certificate_type} label="Tipo" onChange={(e) => setForm({ ...form, certificate_type: e.target.value })}>
                {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Título" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              size="small"
              options={patientOptions}
              getOptionLabel={(o: any) => `${o.full_name || ''}${o.cpf ? ` - CPF: ${o.cpf}` : ''}`}
              onInputChange={(_, v, reason) => {
                if (reason !== 'reset') setPatientSearch(v);
              }}
              onChange={(_, v) => {
                setForm({ ...form, patient_id: v?.id || '' });
                if (v) {
                  lastPatientSearchRef.current = '';
                  setPatientSearch('');
                }
              }}
              value={patientOptions.find((p: any) => p.id === form.patient_id) || null}
              renderInput={(params) => <TextField {...params} label="Paciente" />}
              isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
              filterOptions={(x) => x}
              noOptionsText="Nenhum paciente encontrado"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              size="small"
              options={profOptions}
              getOptionLabel={(o: any) => `${o.full_name || ''} — ${o.registration_type || 'CRM'} ${o.registration_number || ''}`}
              onInputChange={(_, v, reason) => {
                if (reason !== 'reset') setProfSearch(v);
              }}
              onChange={(_, v) => {
                setForm({ ...form, professional_id: v?.id || '' });
                if (v) {
                  lastProfSearchRef.current = '';
                  setProfSearch('');
                }
              }}
              value={profOptions.find((p: any) => p.id === form.professional_id) || null}
              renderInput={(params) => <TextField {...params} label="Profissional" />}
              isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
              filterOptions={(x) => x}
              noOptionsText="Nenhum profissional encontrado"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Data de emissão" value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Dias de afastamento" value={form.days_off}
              onChange={(e) => setForm({ ...form, days_off: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="CID" value={form.cid_code}
              onChange={(e) => setForm({ ...form, cid_code: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date" label="Data início" value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date" label="Data fim" value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Descrição do CID" value={form.cid_description}
              onChange={(e) => setForm({ ...form, cid_description: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={6} label="Conteúdo do atestado" value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Atesto para os devidos fins que..." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2} label="Observações" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>
          {saving ? <CircularProgress size={22} /> : 'Salvar e anexar à evolução'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CertificateFormDialog;
